/**
 * Iframe context helpers.
 *
 * On the iframe-embeddable page, the Blade layout injects `window.IframeApp`
 * (see resources/views/iframe/application.blade.php). On the normal site this
 * object does not exist, so every helper here is a no-op / falsy — the shared
 * form code that calls these (functions.js, IframeSmsVerification.vue) behaves
 * exactly as before when not embedded.
 */

export function getIframeContext() {
    if (typeof window === 'undefined') {
        return null;
    }
    return window.IframeApp && window.IframeApp.iframe ? window.IframeApp : null;
}

export function isIframe() {
    return !!getIframeContext();
}

export function iframeToken() {
    return getIframeContext()?.iframe_token ?? null;
}

/**
 * Attribution/tracking fields captured from the iframe URL at page load
 * (utm_*, gclid/fbclid/ttclid/msclkid, affiliate_sub*, etc.). Forwarded as
 * plain passthrough at submit; `affiliate_id` is NOT here (it is signed into
 * the iframe token). Empty object on the normal site.
 *
 * @returns {Object<string, string>}
 */
export function iframeTracking() {
    return getIframeContext()?.tracking ?? {};
}

/**
 * Optional form features (step groups) this iframe renders, resolved server-side
 * from config + the per-affiliate map. See app/Helpers/IframeFeatures.php. Empty
 * object on the normal site, which keeps every step (only an explicit `false`
 * drops one).
 *
 * @returns {Object<string, boolean>}
 */
export function iframeFeatures() {
    return getIframeContext()?.features ?? {};
}

/**
 * The `dev_step` value from the iframe's own URL, or null.
 *
 * Dev-tool parity with the site form, where this param reaches app state via
 * getUrlParameters(). That helper also lifts affiliate_id and the contact
 * fields off the query string — exactly what the iframe resolves server-side
 * instead — so only the dev param is read here.
 *
 * Reading it does not switch anything on: LcForm still requires APP_DEBUG
 * (`window.App.app_debug`) as well, which is the same rule the site form uses,
 * and is off in production.
 *
 * @param {string} [search] injectable for testing; defaults to the live one
 * @returns {string|null}
 */
export function iframeDevStep(search) {
    const qs = typeof search === 'string'
        ? search
        : (typeof window !== 'undefined' ? window.location.search : '');

    try {
        const value = new URLSearchParams(qs).get('dev_step');
        return value !== null && value !== '' ? value : null;
    } catch (_) {
        return null;
    }
}

/**
 * Whether to force Flinks demo mode (`?flinks_demo=1`) in the iframe.
 *
 * The site lifts `flinks_demo` off the URL for anyone; the iframe never reads
 * form fields from its URL, so this is the one exception, and only with
 * APP_DEBUG on (same gate as dev_step) — it exists to QA the nested Flinks
 * iframe without a lead that meets the lender criteria.
 *
 * @param {string} [search] injectable for testing; defaults to the live one
 * @param {boolean} [debug] injectable for testing; defaults to window.App.app_debug
 * @returns {boolean}
 */
export function iframeFlinksDemo(search, debug) {
    const qs = typeof search === 'string'
        ? search
        : (typeof window !== 'undefined' ? window.location.search : '');
    const appDebug = typeof debug === 'boolean'
        ? debug
        : !!(typeof window !== 'undefined' && window.App?.app_debug);

    if (!appDebug) {
        return false;
    }

    try {
        return new URLSearchParams(qs).get('flinks_demo') === '1';
    } catch (_) {
        return false;
    }
}

export function iframeEndpoints() {
    const ctx = getIframeContext();
    return {
        submit: ctx?.endpoints?.submit ?? '/iframe/application',
        phoneCode: ctx?.endpoints?.phone_code ?? '/iframe/phone/code',
        phoneVerification: ctx?.endpoints?.phone_verification ?? '/iframe/phone/verification',
        flinks: ctx?.endpoints?.flinks ?? '/iframe/v1/flinks',
        flinksExists: ctx?.endpoints?.flinks_exists ?? '/iframe/v1/flinks/exists',
    };
}

/**
 * The signed lead token from the latest real submit response (partial or
 * final). The iframe's Flinks endpoints read the lead id from it rather than
 * from a client-sent uid. Kept here, not in app state, because app state is the
 * submit payload and this never needs to travel back to CMI.
 */
let leadToken = null;

export function iframeLeadToken() {
    return leadToken;
}

/**
 * Keep the newest token from a submit response; a response without one (fake
 * lead, CMI error) leaves the last good token in place.
 *
 * @param {object|null|undefined} response
 */
export function rememberIframeLeadToken(response) {
    const token = response?.lead_token;
    if (typeof token === 'string' && token !== '') {
        leadToken = token;
    }
}

/** Test helper: forget the stored lead token. */
export function resetIframeLeadToken() {
    leadToken = null;
}

/**
 * Origin of the Flinks Connect iframe, for checking where a message came from.
 *
 * @param {string} src the Flinks iframe src
 * @returns {string|null}
 */
export function flinksOrigin(src) {
    try {
        return new URL(src).origin;
    } catch (_) {
        return null;
    }
}

/**
 * Whether FlinksiFrame should act on a postMessage event.
 *
 * Embedded, our page sits inside a partner's page, which can postMessage into
 * it: an unchecked listener would let it fake a Flinks REDIRECT, attach a made
 * up loginId to the lead and skip the step. So in the iframe a message counts
 * only when it comes from the Flinks origin, or from our own redirect bridge
 * (/iframe/{v}/flinks/done) running inside the Flinks frame we created. The
 * site keeps accepting every message, unchanged.
 *
 * @param {MessageEvent} event
 * @param {object} options
 * @param {string|null} options.flinksOrigin origin of the Flinks iframe src
 * @param {Window|null} [options.frameWindow] contentWindow of that iframe
 * @param {string} [options.ownOrigin] defaults to this page's origin
 * @param {boolean} [options.embedded] defaults to isIframe()
 * @returns {boolean}
 */
export function acceptsFlinksMessage(event, { flinksOrigin: expectedOrigin = null, frameWindow = null, ownOrigin, embedded = isIframe() } = {}) {
    if (!embedded) {
        return true;
    }

    if (expectedOrigin && event?.origin === expectedOrigin) {
        return true;
    }

    const self = ownOrigin ?? (typeof window !== 'undefined' ? window.location.origin : null);

    return !!self && !!frameWindow && event?.origin === self && event?.source === frameWindow;
}

/**
 * Extra Flinks Connect params for the iframe.
 *
 * Flinks posts its events to window.top: the partner's page once we are
 * embedded, so our form never sees the REDIRECT carrying the loginId. With
 * innerRedirect Flinks instead navigates its own frame to redirectUrl, our
 * same-origin bridge page, which posts the result to its parent (us). Empty on
 * the site, which keeps listening to Flinks directly.
 *
 * @returns {string} query-string suffix, starting with '&', or ''
 */
export function iframeFlinksRedirectParams() {
    const ctx = getIframeContext();
    const bridge = ctx?.endpoints?.flinks_done;
    if (!ctx || !bridge) {
        return '';
    }

    return `&redirectUrl=${encodeURIComponent(bridge)}&innerRedirect=true`;
}

/**
 * Fire the CMI conversion pixel client-side (a `<script src>` load, matching the
 * normal flow's /redirect interstitial). The normal form fires this on /redirect
 * from session state; the stateless iframe never reaches that, so it must fire
 * the pixel itself from the value in the submit response. Without this, the
 * partner (who is the affiliate) is never credited for the conversion.
 *
 * @param {string|null|undefined} pixelUrl
 * @returns {boolean} whether a pixel was actually injected
 */
/**
 * Append the iframe display context to a results URL so the slim /thank-you
 * renders in the same brand/chrome/language as the form. Adds `iframe=1` plus
 * the display params (brand, bgcolor, header, footer) carried on the current
 * iframe page URL, and `lang` set to the language the form actually resolved.
 *
 * `lang` must be the resolved locale, not just a copy of the URL's `lang`: the
 * form can be French with no `lang` in its URL (a `/pret` slug, or a brand whose
 * default is French). The results page then has nothing but the session to go
 * on, and an iframe on a partner site usually has no session — it would render
 * in the deployment brand's language instead.
 *
 * `search` and `locale` are injectable for testing; they default to the live ones.
 *
 * @param {string} url
 * @param {string} [search]
 * @param {string|null} [locale]
 * @returns {string}
 */
export function withIframeContext(url, search, locale) {
    if (typeof url !== 'string' || url === '') {
        return url;
    }

    const qs = typeof search === 'string'
        ? search
        : (typeof window !== 'undefined' ? window.location.search : '');
    const resolvedLocale = locale === undefined ? (getIframeContext()?.locale ?? null) : locale;
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost';

    try {
        const here = new URLSearchParams(qs);
        const target = new URL(url, origin);
        target.searchParams.set('iframe', '1');
        for (const key of ['brand', 'bgcolor', 'header', 'footer', 'lang']) {
            const value = here.get(key);
            if (value !== null && value !== '') {
                target.searchParams.set(key, value);
            }
        }
        if (typeof resolvedLocale === 'string' && resolvedLocale !== '') {
            target.searchParams.set('lang', resolvedLocale);
        }
        return target.toString();
    } catch (_) {
        return url;
    }
}

/**
 * Where the iframe goes once the form completes, or null to stay in place.
 *
 * A normal submit returns `resultsUrl`. A submit that got no answer gets develop's
 * lost-response fallback instead: `thankYouUrl` (`/thank-you?unique_id=…&recovered=1`).
 * Both go through withIframeContext — without it the fallback would land on the
 * full marketing thank-you inside the partner's frame. No URL at all (e.g. the
 * phone-gate fake lead, or a lost response before any partial gave us an id)
 * leaves the in-place ProcessedApplication to render.
 *
 * @param {object|null} processedApplication
 * @param {string} [search]
 * @param {string|null} [locale]
 * @returns {string|null}
 */
export function iframeCompletionUrl(processedApplication, search, locale) {
    const target = processedApplication?.resultsUrl || processedApplication?.thankYouUrl || null;

    return target ? withIframeContext(target, search, locale) : null;
}

/**
 * What IframeApp should do with the form's completion, given where it stands.
 *
 * "completed" and the submit's result are separate signals that can arrive in
 * either order: a double trigger on the last step (e.g. clicking an
 * auto-advancing choice twice) emits "completed" while the submit is still in
 * flight. Acting on "completed" alone saw an empty result, skipped the pixel
 * and the results page, and later left ProcessedApplication to follow the
 * recovery link without the iframe context. So: wait until both are in.
 *
 *   - 'waiting'  : not completed yet, or completed but no result yet
 *   - 'navigate' : go to `url` (results page, or the lost-response recovery)
 *   - 'in-place' : a result with nowhere to go (e.g. the phone-gate fake lead)
 *
 * @param {boolean} completed
 * @param {object|null} processedApplication
 * @param {string} [search]
 * @param {string|null} [locale]
 * @returns {{ state: 'waiting'|'navigate'|'in-place', url: string|null }}
 */
export function iframeCompletionState(completed, processedApplication, search, locale) {
    const hasResult = !!processedApplication
        && typeof processedApplication === 'object'
        && Object.keys(processedApplication).length > 0;

    if (!completed || !hasResult) {
        return { state: 'waiting', url: null };
    }

    const url = iframeCompletionUrl(processedApplication, search, locale);

    return url ? { state: 'navigate', url } : { state: 'in-place', url: null };
}

export function fireConversionPixel(pixelUrl) {
    if (typeof document === 'undefined' || typeof pixelUrl !== 'string' || pixelUrl.trim() === '') {
        return false;
    }

    const script = document.createElement('script');
    script.src = pixelUrl;
    script.async = true;
    document.head.appendChild(script);

    return true;
}
